import React, { type RefObject } from 'react';
import {
  DefaultEventHandlers,
  Positioner,
  ROOT_NODE,
  type CreateHandlerOptions,
  type DragTarget,
  type Indicator,
  type NodeTree,
} from '@craftjs/core';
import type { PanZoomState } from '@/store/zoom/panZoomSlice';
import { store as appStore } from '@/store/store';
import { showToast } from '@/store/notifications/slice';
import { CONTENT_BLOCK_NAME, POPUP_NODE_NAME } from '@/utils/constants';

export const NODE_DRAG_START = 'node-drag-start';
export const NODE_DRAG_END = 'node-drag-end';
export const DRAGGED_ELEMENT_CHANGE = 'draggedElementChange';

// Extend Window interface for drag state
declare global {
  interface Window {
    __draggedElementDisplayName?: string | null;
    __draggedElementDimensions?: { width: number; height: number } | null;
  }
}

type ZoomHandlersOpts = {
  getZoom: () => RefObject<PanZoomState>; // return your current zoom (e.g., 0.5, 1, 2)
};

export class ZoomAnchoredHandlers extends DefaultEventHandlers<ZoomHandlersOpts> {
  positioner: Positioner | null = null;
  currentSelectedElementIds: string[] = [];

  private isPopupDragTarget(dragTarget: DragTarget | null | undefined): boolean {
    if (dragTarget?.type === 'new') {
      const rootNode = dragTarget.tree?.nodes?.[dragTarget.tree?.rootNodeId];
      return rootNode?.data?.displayName === POPUP_NODE_NAME;
    }

    return false;
  }

  private getRootInsertIndex(): number {
    try {
      return this.options.store.query.node(ROOT_NODE).get()?.data?.nodes?.length || 0;
    } catch {
      return 0;
    }
  }

  handlers() {
    const store = this.options.store;
    const base = super.handlers();
    const makeScaledDragImage = (
      e: DragEvent,
      doms: HTMLElement[],
      zoom: number,
      forceSingle: boolean,
    ): HTMLElement => {
      if (doms.length === 1 || forceSingle) {
        const rect = doms[0].getBoundingClientRect();
        const shadow = doms[0].cloneNode(true) as HTMLElement;
        shadow.style.position = 'absolute';
        shadow.style.left = '-100%';
        shadow.style.top = '-100%';
        shadow.style.width = `${rect.width / zoom}px`;
        shadow.style.height = `${rect.height / zoom}px`;
        shadow.style.pointerEvents = 'none';
        shadow.style.zoom = zoom.toString();
        shadow.classList.add('drag-shadow');
        document.body.appendChild(shadow);
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        e.dataTransfer?.setDragImage(shadow, offsetX, offsetY);
        return shadow;
      }

      const rects = doms.map((d) => d.getBoundingClientRect());
      const minLeft = Math.min(...rects.map((r) => r.left));
      const minTop = Math.min(...rects.map((r) => r.top));
      const maxRight = Math.max(...rects.map((r) => r.right));
      const maxBottom = Math.max(...rects.map((r) => r.bottom));
      const width = maxRight - minLeft;
      const height = maxBottom - minTop;

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-100%';
      container.style.top = '-100%';
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;
      container.style.pointerEvents = 'none';
      container.style.transform = `scale(${zoom})`;
      container.style.transformOrigin = '0 0';
      container.classList.add('drag-shadow-container');

      doms.forEach((dom, i) => {
        const r = rects[i];
        const shadow = dom.cloneNode(true) as HTMLElement;
        shadow.style.position = 'absolute';
        shadow.style.left = `${r.left - minLeft}px`;
        shadow.style.top = `${r.top - minTop}px`;
        shadow.style.width = `${r.width}px`;
        shadow.style.height = `${r.height}px`;
        shadow.classList.add('drag-shadow');
        container.appendChild(shadow);
      });

      document.body.appendChild(container);
      e.dataTransfer?.setDragImage(container, 0, 0);
      return container;
    };
    const handleSetBoundaryIndicators = (isDragging: boolean) => {
      if (isDragging) {
        store.query
          .node('ROOT')
          .descendants()
          .forEach((nodeId: string) => {
            store.query.node(nodeId).get().dom?.classList.add('active-tree');
          });
      }
    };

    return {
      ...base,

      drag: (el: HTMLElement, id: string) => {
        if (!store.query.node(id).isDraggable()) return () => {};
        el.setAttribute('draggable', 'true');

        const unbindStart = this.addCraftEventListener(el, 'dragstart', (e) => {
          handleSetBoundaryIndicators(true);

          e.craft.stopPropagation();

          // For canvas drags, set the source as 'canvas'
          // Layers panel will set this to 'sidebar' via mousedown listener
          if (!window.__lastDragSource) {
            window.__lastDragSource = 'canvas';
          }

          const { query, actions } = store;
          let selectedElementIds = query.getEvent('selected').all();

          const isMultiSelect = this.options.isMultiSelectEnabled(e as unknown as MouseEvent);
          const isNodeAlreadySelected = this.currentSelectedElementIds.includes(id);
          if (!isNodeAlreadySelected) {
            if (isMultiSelect) {
              selectedElementIds = [...selectedElementIds, id];
            } else {
              selectedElementIds = [id];
            }
            store.actions.setNodeEvent('selected', selectedElementIds);
          }

          actions.setNodeEvent('dragged', selectedElementIds);

          const selectedDOMs = selectedElementIds
            .map((nid) => query.node(nid).get().dom as HTMLElement | null)
            .filter((el): el is HTMLElement => !!el);
          const zoom = this.options.getZoom ? this.options.getZoom().current?.scale : 1;

          this.draggedElementShadow = makeScaledDragImage(
            e,
            selectedDOMs,
            zoom,
            DefaultEventHandlers.forceSingleDragShadow,
          );

          this.dragTarget = { type: 'existing', nodes: selectedElementIds };
          this.positioner = new Positioner(this.options.store, this.dragTarget);

          // Store dragged element dimensions for DropTargetIndicator
          if (selectedDOMs.length === 1) {
            const rect = selectedDOMs[0].getBoundingClientRect();
            const scale = zoom || 1;
            window.__draggedElementDimensions = {
              width: rect.width / scale,
              height: rect.height / scale,
            };
          } else {
            window.__draggedElementDimensions = null;
          }

          // Store dragged element displayName globally for DropTargetIndicator
          const node = query.node(id).get();
          window.__draggedElementDisplayName = node?.data?.displayName || null;
          window.dispatchEvent(
            new CustomEvent(DRAGGED_ELEMENT_CHANGE, {
              detail: {
                displayName: window.__draggedElementDisplayName,
                dimensions: window.__draggedElementDimensions,
              },
            }),
          );

          window.dispatchEvent(new CustomEvent(NODE_DRAG_START));
        });

        const unbindDragEnd = this.addCraftEventListener(el, 'dragend', (e) => {
          window.dispatchEvent(new CustomEvent(NODE_DRAG_END));
          handleSetBoundaryIndicators(false);
          e.craft.stopPropagation();

          window.__draggedElementDisplayName = null;
          window.__draggedElementDimensions = null;
          window.dispatchEvent(
            new CustomEvent(DRAGGED_ELEMENT_CHANGE, {
              detail: { displayName: null, dimensions: null },
            }),
          );

          this.finishDrop((dragTarget, indicator) => {
            if (dragTarget.type === 'new') {
              return;
            }

            const parentId = indicator.placement.parent.id;

            const allNodesExist = dragTarget.nodes.every((nodeId: string) => {
              try {
                return store.query.node(nodeId).get() !== null;
              } catch {
                return false;
              }
            });

            if (!allNodesExist) {
              return;
            }

            try {
              if (!store.query.node(parentId).get()) {
                return;
              }
            } catch {
              return;
            }

            const index =
              indicator.placement.index + (indicator.placement.where === 'after' ? 1 : 0);

            try {
              store.actions.move(dragTarget.nodes, parentId, index);
            } catch (error) {
              console.error('Move operation failed:', error);
              appStore.dispatch(
                showToast({
                  text: 'Unable to move element to this location.',
                  state: 'error',
                  icon: 'warning',
                }),
              );
            }
          });
        });

        return () => {
          el.setAttribute('draggable', 'false');
          unbindStart();
          unbindDragEnd();
        };
      },

      create: (
        el: HTMLElement,
        userElement: React.ReactElement | (() => NodeTree | React.ReactElement),
        options?: Partial<CreateHandlerOptions>,
      ) => {
        el.setAttribute('draggable', 'true');

        const unbindDragStart = this.addCraftEventListener(el, 'dragstart', (e) => {
          handleSetBoundaryIndicators(true);
          e.craft.stopPropagation();
          let tree;
          if (typeof userElement === 'function') {
            const result = userElement();
            if (React.isValidElement(result)) {
              tree = store.query.parseReactElement(result).toNodeTree();
            } else {
              tree = result;
            }
          } else {
            tree = store.query.parseReactElement(userElement).toNodeTree();
          }

          if (!tree || !tree.nodes || !tree.rootNodeId) {
            console.error('Invalid NodeTree returned from userElement factory:', {
              tree,
              hasNodes: tree?.nodes !== undefined,
              hasRootNodeId: tree?.rootNodeId !== undefined,
            });
            handleSetBoundaryIndicators(false);
            return;
          }

          const dom = e.currentTarget as HTMLElement;
          const zoom = this.options.getZoom ? this.options.getZoom().current?.scale : 1;

          this.draggedElementShadow = makeScaledDragImage(
            e,
            [dom],
            zoom,
            DefaultEventHandlers.forceSingleDragShadow,
          );
          this.dragTarget = {
            type: 'new',
            tree,
          };

          // Store dragged element info globally for DropTargetIndicator
          const rootNode = tree.nodes[tree.rootNodeId];
          window.__draggedElementDisplayName = rootNode?.data?.displayName || null;
          // New elements don't have canvas dimensions yet — placeholder will use sibling height
          window.__draggedElementDimensions = null;
          window.dispatchEvent(
            new CustomEvent(DRAGGED_ELEMENT_CHANGE, {
              detail: {
                displayName: window.__draggedElementDisplayName,
                dimensions: null,
              },
            }),
          );

          this.positioner = new Positioner(this.options.store, this.dragTarget);

          window.dispatchEvent(new CustomEvent(NODE_DRAG_START));
        });

        const unbindDragEnd = this.addCraftEventListener(el, 'dragend', (e) => {
          window.dispatchEvent(new CustomEvent(NODE_DRAG_END));
          handleSetBoundaryIndicators(false);
          e.craft.stopPropagation();
          window.__draggedElementDisplayName = null;
          window.__draggedElementDimensions = null;
          window.dispatchEvent(
            new CustomEvent(DRAGGED_ELEMENT_CHANGE, {
              detail: { displayName: null, dimensions: null },
            }),
          );
          this.finishDrop((dragTarget, indicator) => {
            if (dragTarget.type === 'existing') {
              return;
            }

            const isPopupDragTarget = this.isPopupDragTarget(dragTarget);

            if (isPopupDragTarget) {
              store.actions.addNodeTree(dragTarget.tree, ROOT_NODE, this.getRootInsertIndex());
            } else {
              const index =
                indicator.placement.index + (indicator.placement.where === 'after' ? 1 : 0);
              store.actions.addNodeTree(dragTarget.tree, indicator.placement.parent.id, index);
            }

            if (options && typeof options.onCreate === 'function') {
              options.onCreate(dragTarget.tree);
            }
          });
        });

        return () => {
          el.removeAttribute('draggable');
          unbindDragStart();
          unbindDragEnd();
        };
      },
    };
  }

  private finishDrop(onDropNode: (dragTarget: DragTarget, placement: Indicator) => void) {
    const store = this.options.store;

    if (!this.positioner) {
      return;
    }

    const draggedElementShadow = this.draggedElementShadow;

    const indicator = this.positioner.getIndicator();
    const isPopupDragTarget = this.isPopupDragTarget(this.dragTarget);

    if (!indicator || !this.dragTarget || (indicator.error && !isPopupDragTarget)) {
      if (draggedElementShadow?.parentNode) {
        draggedElementShadow.parentNode.removeChild(draggedElementShadow);
      }
      store.actions.setIndicator(null);
      store.actions.setNodeEvent('dragged', '');
      this.positioner.cleanup();
      this.positioner = null;
      return;
    }

    const safeGetNode = (id: string) => {
      try {
        return store.query.node(id).get();
      } catch {
        return null;
      }
    };

    const parentId = indicator.placement.parent.id;
    const parentNode = safeGetNode(parentId);

    if (!parentNode) {
      if (draggedElementShadow?.parentNode) {
        draggedElementShadow.parentNode.removeChild(draggedElementShadow);
      }
      store.actions.setIndicator(null);
      store.actions.setNodeEvent('dragged', '');
      this.positioner.cleanup();
      this.positioner = null;
      return;
    }
    const isPageParent =
      parentId === ROOT_NODE ||
      parentNode?.data?.type === 'Page' ||
      parentNode?.data?.displayName === 'Page';
    const parentChildCount = parentNode?.data?.nodes?.length || 0;
    const isBlankPage = isPageParent && parentChildCount === 0;

    if (this.dragTarget.type === 'existing') {
      const allNodesExist = this.dragTarget.nodes.every((nodeId: string) => {
        return safeGetNode(nodeId) !== null;
      });

      if (!allNodesExist) {
        if (draggedElementShadow?.parentNode) {
          draggedElementShadow.parentNode.removeChild(draggedElementShadow);
        }
        store.actions.setIndicator(null);
        store.actions.setNodeEvent('dragged', '');
        this.positioner.cleanup();
        this.positioner = null;
        return;
      }
    }

    let isContainer = false;
    if (this.dragTarget.type === 'new') {
      const tree = this.dragTarget.tree as NodeTree;
      const rootNode = tree.nodes[tree.rootNodeId];
      isContainer = rootNode?.data?.displayName === CONTENT_BLOCK_NAME;
    } else if (this.dragTarget.type === 'existing') {
      isContainer = this.dragTarget.nodes.every((nodeId: string) => {
        const node = safeGetNode(nodeId);
        return node?.data?.displayName === CONTENT_BLOCK_NAME;
      });
    }

    if (!isPopupDragTarget && isPageParent && !isContainer) {
      appStore.dispatch(
        showToast({
          text: isBlankPage
            ? 'Cannot add element directly. Please add a container first.'
            : 'Only containers can be added directly to the page.',
          state: 'error',
          icon: 'warning',
        }),
      );

      if (draggedElementShadow?.parentNode) {
        draggedElementShadow.parentNode.removeChild(draggedElementShadow);
      }
      store.actions.setIndicator(null);
      store.actions.setNodeEvent('dragged', '');
      this.positioner.cleanup();
      this.positioner = null;
      return;
    }

    onDropNode(this.dragTarget, indicator);

    if (draggedElementShadow?.parentNode) {
      draggedElementShadow.parentNode.removeChild(draggedElementShadow);
    }

    store.actions.setIndicator(null);
    store.actions.setNodeEvent('dragged', '');
    this.positioner.cleanup();

    this.positioner = null;
  }
}
